import { BookOpen, Link2, Loader2 } from 'lucide-react'

interface MendeleyConnectProps {
  onConnect: () => void
  isConnecting: boolean
  error?: string
}

export default function MendeleyConnect({ onConnect, isConnecting, error }: MendeleyConnectProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-8 max-w-lg mx-auto">
      <div className="text-center">
        {/* Mendeley branding area */}
        <div className="mx-auto w-20 h-20 rounded-2xl bg-red-50 flex items-center justify-center mb-6">
          <BookOpen className="h-10 w-10 text-red-500" />
        </div>

        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Connect Your Mendeley Account
        </h2>
        <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
          Import and export your research library seamlessly. Link your Mendeley account to sync
          references, organize collections, and access your research from the academic portal.
        </p>

        <div className="space-y-4 text-left bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-acad-100 flex items-center justify-center mt-0.5">
              <span className="text-xs font-semibold text-acad-700">1</span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Import references</p>
              <p className="text-xs text-gray-500">Bring your Mendeley library into the portal</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-acad-100 flex items-center justify-center mt-0.5">
              <span className="text-xs font-semibold text-acad-700">2</span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Export discoveries</p>
              <p className="text-xs text-gray-500">Save portal resources to your Mendeley library</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-acad-100 flex items-center justify-center mt-0.5">
              <span className="text-xs font-semibold text-acad-700">3</span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Stay in sync</p>
              <p className="text-xs text-gray-500">Your research library, always up to date</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          onClick={onConnect}
          disabled={isConnecting}
          className="inline-flex items-center gap-2 rounded-lg bg-acad-500 px-6 py-2.5 text-sm font-medium text-white hover:bg-acad-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isConnecting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              <Link2 className="h-4 w-4" />
              Connect with Mendeley
            </>
          )}
        </button>
      </div>
    </div>
  )
}
